import { useMemo, useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Search, Trash2, Loader2, AlertTriangle, Lock, Image as ImageIcon, Download, Plus, Pencil } from 'lucide-react'
import { iconsApi, type IconItem, type IconUsage } from '@/features/objects/api/icons'
import { ImageIntake } from '@/components/shared/ImageIntake'
import { composeIconOnFrame, toIconFilename } from '@/features/objects/lib/iconFraming'
import { Label } from '@/components/ui/label'
import { buildIconPath } from '@/lib/imagePaths'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import { EntityIcon } from '@/components/ui/entity-icon'
import { useCan } from '@/hooks/useCan'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'

// Human-readable label for the icon name (strips the vsicon prefix / .png like the picker does).
function prettyName(icon: IconItem): string {
    return (icon.description || icon.filename || '').replace(/^vsicon/, '').replace(/\.png$/, '')
}

export function IconsAdminPage() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [search, setSearch] = useState('')
    const [deleteTarget, setDeleteTarget] = useState<IconItem | null>(null)
    const [exporting, setExporting] = useState(false)
    // The screen is open to every graphics.read; a change is offered only where the API allows it.
    // Adding and replacing an image are both an upload (presign + register): graphics.create.
    const canCreate = useCan('graphics.create')
    const canDelete = useCan('graphics.delete')

    // ── Add / replace ────────────────────────────────────────────────────────
    // Until now an icon could only be added from inside the object picker, which
    // meant the screen that manages the library was the one place you could not
    // put anything into it. `replaceTarget` set = replacing that icon's image;
    // null with the dialog open = adding a new one.
    const [uploadOpen, setUploadOpen] = useState(false)
    const [replaceTarget, setReplaceTarget] = useState<IconItem | null>(null)
    const [composing, setComposing] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [pendingBlob, setPendingBlob] = useState<Blob | null>(null)
    const [pendingPreview, setPendingPreview] = useState<string | null>(null)
    const [pendingName, setPendingName] = useState('')
    const [pendingFallbackName, setPendingFallbackName] = useState('icon')
    const [pendingTags, setPendingTags] = useState('')
    const [pendingDescription, setPendingDescription] = useState('')

    const resetPending = useCallback(() => {
        setPendingBlob(null)
        setPendingPreview(null)
        setPendingName('')
        setPendingFallbackName('icon')
        setPendingTags('')
        setPendingDescription('')
        setComposing(false)
    }, [])

    const closeUpload = useCallback(() => {
        setUploadOpen(false)
        setReplaceTarget(null)
        resetPending()
    }, [resetPending])

    // Frame the incoming image exactly as the object picker does, so an icon added
    // here is indistinguishable from one added there.
    const stageFile = useCallback(
        async (file: File) => {
            setComposing(true)
            try {
                const { blob, dataUrl } = await composeIconOnFrame(file)
                setPendingBlob(blob)
                setPendingPreview(dataUrl)
                setPendingName((prev) => prev || file.name.replace(/\.[^.]+$/, ''))
                setPendingFallbackName(toIconFilename(file.name, 'icon').replace(/\.png$/, ''))
            } catch {
                toast({
                    variant: 'destructive',
                    title: t('icons.frameFailedTitle', 'Could not process image'),
                    description: t('icons.frameFailed', 'The image could not be framed. Try a different one.'),
                })
            } finally {
                setComposing(false)
            }
        },
        [toast, t],
    )

    const openAdd = useCallback(() => { resetPending(); setReplaceTarget(null); setUploadOpen(true) }, [resetPending])
    const openReplace = useCallback((icon: IconItem) => {
        resetPending()
        setReplaceTarget(icon)
        setPendingTags(icon.tags || '')
        setPendingDescription(icon.description || '')
        setUploadOpen(true)
    }, [resetPending])

    // The filename that will actually be written, shown live under the Name field.
    // Replacing keeps the existing name, so say THAT rather than a name derived from
    // whatever is typed — the field is hidden in replace mode for the same reason.
    // Shown as soon as there is anything to show, not only once an image is staged.
    // Icons are always framed to PNG, so unlike shapes the extension is known before a
    // file exists and the whole filename can build as you type. Waiting for the image
    // made the field look inert while someone was typing into it.
    const finalIconFilename = replaceTarget
        ? replaceTarget.filename
        : pendingName.trim() || pendingBlob
            ? toIconFilename(pendingName, pendingFallbackName)
            : ''

    const handleConfirmUpload = useCallback(async () => {
        if (!pendingBlob) return
        // The same value shown under the Name field — deliberately not recomputed here.
        // Replacing keeps the existing filename because the filename IS the S3 key, and
        // every object referencing this icon resolves through it, so writing to the same
        // key updates every referrer at once. Renaming would orphan them.
        const filename = finalIconFilename
        const file = new File([pendingBlob], filename, { type: 'image/png' })
        setUploading(true)
        try {
            // overwrite is implied when replacing; when adding, a clash is a real
            // question for the user, so let the 409 come back and ask.
            await iconsApi.upload(file, pendingDescription || undefined, pendingTags || undefined, !!replaceTarget)
            queryClient.invalidateQueries({ queryKey: ['icons'] })
            queryClient.invalidateQueries({ queryKey: ['objects'] })
            toast({
                title: replaceTarget
                    ? t('icons.replacedTitle', 'Icon updated')
                    : t('icons.addedTitle', 'Icon added'),
                description: replaceTarget
                    ? t('icons.replacedDescription', '{{filename}} was replaced everywhere it is used.', { filename })
                    : t('icons.addedDescription', '{{filename}} added to the library.', { filename }),
            })
            closeUpload()
        } catch (err: any) {
            if (err?.response?.status === 409) {
                // Adding something that already exists. Confirm, then write over it.
                const existingName = filename
                if (window.confirm(t('icons.overwriteConfirm', '“{{name}}” already exists in the library. Replace it?', { name: existingName }))) {
                    try {
                        await iconsApi.upload(file, pendingDescription || undefined, pendingTags || undefined, true)
                        queryClient.invalidateQueries({ queryKey: ['icons'] })
                        queryClient.invalidateQueries({ queryKey: ['objects'] })
                        toast({ title: t('icons.replacedTitle', 'Icon updated') })
                        closeUpload()
                        return
                    } catch (e: any) {
                        toast({
                            variant: 'destructive',
                            title: t('icons.uploadFailedTitle', 'Upload failed'),
                            description: e?.response?.data?.message || e?.message || t('icons.tryAgain', 'Please try again.'),
                        })
                        return
                    }
                }
                return
            }
            toast({
                variant: 'destructive',
                title: t('icons.uploadFailedTitle', 'Upload failed'),
                description: err?.response?.data?.message || err?.message || t('icons.tryAgain', 'Please try again.'),
            })
        } finally {
            setUploading(false)
        }
    }, [pendingBlob, finalIconFilename, pendingTags, pendingDescription, replaceTarget, queryClient, toast, t, closeUpload])

    // Every file in the library, not the filtered grid: the search box narrows what is on
    // screen, and an export that silently honoured it would hand over a subset that looks
    // complete. The estate export already ships only referenced icons — this one is the
    // whole library, which is the gap it exists to fill.
    const handleExport = async () => {
        setExporting(true)
        try {
            const response = await fetch('/api/icons/export', { headers: authHeaders() })
            if (!response.ok) throw new Error(`Export failed (${response.status})`)
            // Through a blob rather than a plain link: the request needs the auth header.
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `VaultSentinelIcons-${new Date().toISOString().slice(0, 10)}.zip`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch (e: any) {
            toast({
                title: t('icons.exportFailed', 'Export failed'),
                description: e?.message || String(e),
                variant: 'destructive',
            })
        } finally {
            setExporting(false)
        }
    }

    const { data: icons = [], isLoading } = useQuery({
        queryKey: ['icons'],
        queryFn: async () => {
            const res = await iconsApi.list()
            return res.data.data
        },
    })

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return icons
        return icons.filter(
            (i) =>
                i.filename?.toLowerCase().includes(term) ||
                i.description?.toLowerCase().includes(term) ||
                i.tags?.toLowerCase().includes(term),
        )
    }, [icons, search])

    // Usage is fetched when a delete is requested, so the confirmation can warn how many
    // objects/services/runbooks reference the icon before it's removed.
    const { data: usage, isFetching: usageLoading } = useQuery({
        queryKey: ['icon-usage', deleteTarget?.id],
        enabled: !!deleteTarget,
        queryFn: async () => {
            const res = await iconsApi.usage(deleteTarget!.id)
            return res.data.data as IconUsage
        },
    })

    const del = useMutation({
        mutationFn: (id: number) => iconsApi.remove(id),
        onSuccess: (res) => {
            const { filename, cleared } = res.data.data
            const clearedTotal = Object.values(cleared || {}).reduce((a, b) => a + Number(b || 0), 0)
            queryClient.invalidateQueries({ queryKey: ['icons'] })
            queryClient.invalidateQueries({ queryKey: ['objects'] })
            setDeleteTarget(null)
            toast({
                title: t('icons.deletedTitle', 'Icon deleted'),
                description: clearedTotal > 0
                    ? t('icons.deletedAndClearedDescription', '{{filename}} removed and cleared from {{count}} item.', { filename, count: clearedTotal })
                    : t('icons.deletedDescription', '{{filename}} removed from the library.', { filename }),
            })
        },
        onError: (err: any) => {
            toast({
                variant: 'destructive',
                title: t('icons.deleteErrorTitle', 'Could not delete icon'),
                description: err?.response?.data?.message || err?.message || t('icons.tryAgain', 'Please try again.'),
            })
        },
    })

    return (
        <>
            <div className="container mx-auto p-6 space-y-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-2">
                            <ImageIcon className="h-7 w-7" />
                            {t('icons.title', 'Icons')}
                        </h1>
                        <p className="text-muted-foreground mt-1 max-w-2xl">
                            {t('icons.subtitle', 'The shared icon library used across objects, services and runbooks. The same icon is often reused by many objects — deleting one removes it from every item that uses it.')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative max-w-sm flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t('icons.search', 'Search icons by name, description or tag…')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Button variant="outline" onClick={handleExport} disabled={exporting}>
                        {exporting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4 mr-2" />
                        )}
                        {t('icons.exportAll', 'Export all icons')}
                    </Button>
                    {canCreate && (
                        <Button onClick={openAdd}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t('icons.add', 'Add icon')}
                        </Button>
                    )}
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        {t('icons.loading', 'Loading icons…')}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        {search ? t('icons.noSearchResults', 'No icons match your search.') : t('icons.empty', 'No icons in the library yet.')}
                    </div>
                ) : (
                    <>
                        <p className="text-xs text-muted-foreground">{t('icons.iconCount', '{{count}} icon', { count: filtered.length })}</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                            {filtered.map((icon) => (
                                <div
                                    key={icon.id}
                                    className="group relative flex flex-col items-center gap-1.5 rounded-lg border p-3 transition-colors hover:bg-accent/40"
                                >
                                    <EntityIcon
                                        src={buildIconPath(icon.filename)}
                                        alt={icon.filename}
                                        className="h-14 w-14"
                                    />
                                    <span className="w-full truncate text-center text-xs font-medium" title={icon.filename}>
                                        {prettyName(icon)}
                                    </span>
                                    {icon.tags && (
                                        <span className="w-full truncate text-center text-[10px] text-muted-foreground" title={icon.tags}>
                                            {icon.tags}
                                        </span>
                                    )}
                                    {icon.reserved ? (
                                        <span
                                            className="absolute right-1 top-1 rounded p-0.5 text-muted-foreground/50"
                                            title={t('icons.systemIcon', 'System icon — cannot be deleted')}
                                        >
                                            <Lock className="h-3 w-3" />
                                        </span>
                                    ) : (
                                        <>
                                            {canCreate && (
                                                <button
                                                    type="button"
                                                    onClick={() => openReplace(icon)}
                                                    className="absolute left-1 top-1 rounded-md p-1 text-muted-foreground opacity-0 transition hover:bg-accent focus:opacity-100 group-hover:opacity-100"
                                                    title={t('icons.replace', 'Replace image')}
                                                    aria-label={t('icons.replaceNamed', 'Replace image for {{name}}', { name: icon.filename })}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    type="button"
                                                    onClick={() => setDeleteTarget(icon)}
                                                    className="absolute right-1 top-1 rounded-md p-1 text-muted-foreground opacity-0 transition hover:bg-destructive hover:text-destructive-foreground focus:opacity-100 group-hover:opacity-100"
                                                    title={t('icons.delete', 'Delete icon')}
                                                    aria-label={t('icons.deleteNamed', 'Delete {{name}}', { name: icon.filename })}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Add / replace an icon — file or clipboard, framed like the object picker */}
            <Dialog open={uploadOpen} onOpenChange={(open) => { if (!open && !uploading) closeUpload() }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {replaceTarget
                                ? t('icons.replaceTitle', 'Replace “{{name}}”', { name: prettyName(replaceTarget) })
                                : t('icons.addTitle', 'Add an icon')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {replaceTarget && (
                            <p className="text-xs text-muted-foreground">
                                {t(
                                    'icons.replaceExplain',
                                    'The new image is written to the same file, so every object, service and runbook already using this icon picks it up. The name does not change.',
                                )}
                            </p>
                        )}

                        <ImageIntake onFile={stageFile} busy={composing} disabled={uploading} />

                        {composing && (
                            <p className="text-xs text-muted-foreground flex items-center gap-2">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                {t('icons.framing', 'Framing image…')}
                            </p>
                        )}

                        {pendingPreview && (
                            <div className="flex items-center gap-3 rounded-md border p-3">
                                <img src={pendingPreview} alt="" className="h-16 w-16 rounded" />
                                <p className="text-xs text-muted-foreground">
                                    {t('icons.previewNote', 'Scaled and centred on the standard frame so it matches the rest of the library.')}
                                </p>
                            </div>
                        )}

                        {!replaceTarget && (
                            <div className="space-y-1.5">
                                <Label htmlFor="icon-name">{t('icons.nameLabel', 'Name')}</Label>
                                <Input
                                    id="icon-name"
                                    value={pendingName}
                                    onChange={(e) => setPendingName(e.target.value)}
                                    placeholder={pendingFallbackName}
                                />
                                {finalIconFilename && (
                                    <p className="text-[11px] text-muted-foreground">
                                        {t('icons.savedAs', 'Saved as')}{' '}
                                        <span className="font-mono">{finalIconFilename}</span>
                                    </p>
                                )}
                            </div>
                        )}

                        {replaceTarget && finalIconFilename && (
                            <p className="text-[11px] text-muted-foreground">
                                {t('icons.keepsFilename', 'Keeps the filename')}{' '}
                                <span className="font-mono">{finalIconFilename}</span>
                            </p>
                        )}

                        <div className="space-y-1.5">
                            <Label htmlFor="icon-tags">{t('icons.tagsLabel', 'Tags')}</Label>
                            <Input
                                id="icon-tags"
                                value={pendingTags}
                                onChange={(e) => setPendingTags(e.target.value)}
                                placeholder={t('icons.tagsPlaceholder', 'e.g. cisco, switch, network')}
                            />
                            <p className="text-[11px] text-muted-foreground">
                                {t('icons.tagsHelp', 'Tags feed the matcher that auto-assigns icons to new objects. Specific vendor and product words work best.')}
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="icon-description">{t('icons.descriptionLabel', 'Description')}</Label>
                            <Input
                                id="icon-description"
                                value={pendingDescription}
                                onChange={(e) => setPendingDescription(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={closeUpload} disabled={uploading}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={handleConfirmUpload}
                            disabled={!pendingBlob || uploading || composing}
                            title={!pendingBlob ? t('icons.needImage', 'Choose a file or paste an image first') : undefined}
                        >
                            {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {replaceTarget ? t('icons.replaceAction', 'Replace image') : t('icons.addAction', 'Add icon')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation — usage-aware */}
            <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !del.isPending) setDeleteTarget(null) }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {t('icons.deleteConfirmTitle', 'Delete “{{name}}”?', { name: deleteTarget ? prettyName(deleteTarget) : '' })}
                        </DialogTitle>
                    </DialogHeader>

                    {usageLoading ? (
                        <div className="flex items-center justify-center py-6 text-muted-foreground">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            {t('icons.checkingUsage', 'Checking where this icon is used…')}
                        </div>
                    ) : usage ? (
                        <div className="space-y-3 text-sm">
                            {usage.inUse ? (
                                <>
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                                        <span>{t('icons.inUseWarning', 'This icon is in use. Deleting it will remove it from:')}</span>
                                    </div>
                                    <ul className="ml-6 list-disc space-y-0.5">
                                        {usage.counts.objects > 0 && <li><b>{usage.counts.objects}</b> {t('icons.usageObjects', 'object / service', { count: usage.counts.objects })}</li>}
                                        {usage.counts.runbooks > 0 && <li><b>{usage.counts.runbooks}</b> {t('icons.usageRunbooks', 'master runbook', { count: usage.counts.runbooks })}</li>}
                                        {usage.counts.technologies > 0 && <li><b>{usage.counts.technologies}</b> {t('icons.usageTechnologies', 'technology', { count: usage.counts.technologies })}</li>}
                                        {usage.counts.manufacturers > 0 && <li><b>{usage.counts.manufacturers}</b> {t('icons.usageManufacturers', 'manufacturer', { count: usage.counts.manufacturers })}</li>}
                                    </ul>
                                    {usage.sampleObjects.length > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            {t('icons.usageExamples', 'e.g. {{list}}', { list: usage.sampleObjects.slice(0, 6).map((o) => o.objectID).join(', ') })}
                                            {usage.counts.objects > usage.sampleObjects.slice(0, 6).length ? '…' : ''}
                                        </p>
                                    )}
                                    <p className="text-muted-foreground">
                                        {t('icons.usageKeepData', 'Those items keep all their data but will show no icon until you assign a new one.')}
                                    </p>
                                </>
                            ) : (
                                <p className="text-muted-foreground">
                                    {t('icons.notInUse', 'This icon isn’t used by anything. It will be removed from the library.')}
                                </p>
                            )}
                        </div>
                    ) : null}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={del.isPending}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => deleteTarget && del.mutate(deleteTarget.id)}
                            disabled={del.isPending || usageLoading}
                        >
                            {del.isPending ? t('icons.deleting', 'Deleting…') : t('icons.delete', 'Delete icon')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
